import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { answerQuestion, completePlay } from "../gamesApi";

/**
 * Multiple-choice Trivia, one question at a time. The level payload has no
 * correct answers: each answer is sent to the server (bound to the signed
 * play session the page starts via onRoundStart) and the server replies
 * whether it was right and which option was correct. The final score comes
 * from completePlay (server-recorded answers), never from this component.
 */
function Trivia({ questions, gameTitle, levelName, backTo, playToken, playError, onRoundStart, onComplete, completionSlot }) {
  const list = Array.isArray(questions) ? questions : [];
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [phase, setPhase] = useState("question");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const busyRef = useRef(false);
  const questionRef = useRef(null);
  const feedbackRef = useRef(null);
  const resultRef = useRef(null);
  const focusNext = useRef(null);

  useEffect(() => {
    if (onRoundStart) onRoundStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const target = focusNext.current === "feedback" ? feedbackRef.current : focusNext.current === "result" ? resultRef.current : focusNext.current === "question" ? questionRef.current : null;
    focusNext.current = null;
    if (target) target.focus();
  });

  const question = list[index] || null;
  const total = list.length;

  const restart = () => {
    busyRef.current = false;
    setIndex(0);
    setSelected("");
    setFeedback(null);
    setCorrectCount(0);
    setResult(null);
    setError("");
    setPhase("question");
    focusNext.current = "question";
    if (onRoundStart) onRoundStart();
  };

  const submit = (e) => {
    e.preventDefault();
    if (busyRef.current || !question || !selected || feedback || !playToken) return;
    busyRef.current = true;
    setError("");
    setPhase("checking");
    answerQuestion(playToken, question.id, selected).then((res) => {
      busyRef.current = false;
      if (res.ok) {
        setFeedback({ correct: !!res.data.correct, correctOptionId: res.data.correct_option_id });
        setCorrectCount(res.data.correct_count);
        setPhase("question");
        focusNext.current = "feedback";
      } else {
        setError(res.error);
        setPhase("question");
      }
    });
  };

  const finish = () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setError("");
    setPhase("finishing");
    completePlay(playToken, {}).then((res) => {
      busyRef.current = false;
      if (res.ok && res.data.result) {
        setResult(res.data.result);
        setPhase("result");
        focusNext.current = "result";
        if (onComplete) onComplete({ completion: res.data.completion || null });
      } else {
        setError(res.error);
        setPhase("finish_error");
      }
    });
  };

  const next = () => {
    if (index + 1 >= total) {
      finish();
      return;
    }
    setIndex(index + 1);
    setSelected("");
    setFeedback(null);
    setError("");
    focusNext.current = "question";
  };

  if (!total) {
    return (
      <div className="games-panel games-panel--empty" role="alert">
        <p className="games-panel__body">This level has no questions yet.</p>
      </div>
    );
  }

  const back = backTo ? (
    <Link to={backTo.path} className="btn btn-outline-secondary trivia__back">
      {backTo.label}
    </Link>
  ) : null;

  if (playError && phase !== "result") {
    return (
      <div className="games-panel games-panel--error trivia" role="alert">
        <p className="games-panel__body">{playError}</p>
        <button type="button" className="btn btn-danger" onClick={restart}>
          Try again
        </button>
      </div>
    );
  }

  if (!playToken && phase !== "result") {
    return (
      <div className="games-panel trivia" role="status" aria-live="polite">
        <p className="games-panel__body">Getting your questions ready…</p>
      </div>
    );
  }

  if (phase === "result" && result) {
    return (
      <section className="trivia trivia--result" aria-labelledby="trivia-result-title" data-testid="trivia-result">
        <h3 id="trivia-result-title" className="trivia__result-title" tabIndex={-1} ref={resultRef}>
          Level complete!
        </h3>
        <p className="trivia__result-level">
          {gameTitle} · {levelName}
        </p>
        <p className="trivia__score" aria-live="polite">
          You got <strong>{result.correct}</strong> of <strong>{result.total}</strong> correct ({result.percentage}%).
        </p>
        <div className="trivia__actions">
          <button type="button" className="btn btn-danger" onClick={restart}>
            Replay
          </button>
          {back}
        </div>
        {completionSlot}
      </section>
    );
  }

  if (phase === "finishing" || phase === "finish_error") {
    return (
      <div className="games-panel trivia" role={phase === "finish_error" ? "alert" : "status"} aria-live="polite">
        {phase === "finishing" ? (
          <p className="games-panel__body">Adding up your score…</p>
        ) : (
          <React.Fragment>
            <p className="games-panel__body">{error}</p>
            <button type="button" className="btn btn-danger" onClick={finish}>
              Try again
            </button>{" "}
            <button type="button" className="btn btn-outline-secondary" onClick={restart}>
              Start the level again
            </button>
          </React.Fragment>
        )}
      </div>
    );
  }

  const answered = !!feedback;
  const checking = phase === "checking";
  const correctOption = answered ? question.options.find((o) => o.id === feedback.correctOptionId) : null;
  return (
    <section className="trivia" aria-labelledby="trivia-question-title" data-testid="trivia">
      <div className="trivia__progress">
        <span className="trivia__count">
          Question {index + 1} of {total}
        </span>
        <span className="trivia__tally">{correctCount} correct so far</span>
        <div
          className="trivia__bar"
          role="progressbar"
          aria-label="Questions answered"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={index + (answered ? 1 : 0)}
        >
          <span className="trivia__bar-fill" style={{ width: ((index + (answered ? 1 : 0)) * 100) / total + "%" }} />
        </div>
      </div>
      <form className="trivia__form" onSubmit={submit}>
        <fieldset className="trivia__fieldset" disabled={answered || checking}>
          <legend className="trivia__legend">
            <span id="trivia-question-title" className="trivia__question" tabIndex={-1} ref={questionRef}>
              {question.text}
            </span>
          </legend>
          <div className="trivia__options">
            {question.options.map((option) => {
              const isSelected = selected === option.id;
              let state = "";
              if (answered && option.id === feedback.correctOptionId) state = " is-correct";
              else if (answered && isSelected) state = " is-wrong";
              return (
                <label key={option.id} className={"trivia__option" + (isSelected ? " is-selected" : "") + state}>
                  <input
                    type="radio"
                    name={"trivia-" + question.id}
                    value={option.id}
                    checked={isSelected}
                    onChange={() => setSelected(option.id)}
                    className="trivia__radio"
                  />
                  <span className="trivia__option-text">{option.text}</span>
                  {answered && option.id === feedback.correctOptionId ? <span className="trivia__mark">Correct answer</span> : null}
                  {answered && isSelected && !feedback.correct ? <span className="trivia__mark">Your answer</span> : null}
                </label>
              );
            })}
          </div>
        </fieldset>
        {error ? (
          <p className="trivia__error" role="alert">
            {error}
          </p>
        ) : null}
        {!answered ? (
          <button type="submit" className="btn btn-danger trivia__submit" disabled={!selected || checking}>
            {checking ? "Checking…" : "Submit answer"}
          </button>
        ) : null}
      </form>
      {answered ? (
        <div
          className={"trivia__feedback " + (feedback.correct ? "is-correct" : "is-wrong")}
          role="status"
          aria-live="polite"
          tabIndex={-1}
          ref={feedbackRef}
        >
          <p className="trivia__feedback-text">
            {feedback.correct ? "Correct!" : "Not quite."}
            {!feedback.correct && correctOption ? " The correct answer is “" + correctOption.text + "”." : ""}
          </p>
          <button type="button" className="btn btn-danger trivia__next" onClick={next}>
            {index + 1 >= total ? "See results" : "Next question"}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default Trivia;
